import { Fragment } from 'preact';
import { useMemo, useRef, useState, useEffect } from 'preact/hooks';
import { services } from '../app';
import { BODY_IDS, constellationName } from '../astro/solar';
import { knownMeaning } from '../data/arabicSky';
import { BODY_ARABIC } from '../data/arabicTerms';
import { starDesignation } from '../data/catalog';
import { ARABIC_STAR_NAMES, constellationMeaning } from '../data/lore';
import { Vec3, enuToAltAz, refractEnu, vecToRaDec } from '../lib/math';
import { displayName, longName, satStatus } from '../live/satellites';
import { lookingFor } from '../live/saudi';
import { smallBodiesRev } from '../live/smallbodies';
import { Selection, goLive, infoMinimised, panel, place, pointingAt, selection, settings, simNow, viewMode } from '../state';
import { Candidate, VoiceIntent, brightestMatch, listen, parseVoice, prepareVoice, voiceOnDevice, voiceSupported } from '../live/voice';
import { stopNarration } from '../audio/narrator';
import { findPages } from './MorePanel';
import { Sheet } from './chrome';
import { directionWords, whereInSky } from './format';
import { Icon } from './icons';

interface Entry {
  label: string;
  sub: string;
  icon: string;
  sel: Selection;
  keys: string[];
  rank: number;
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[ʿʾ’'`]/g, '')
    // Arabic: ignore short vowels and tatweel, and treat the alif and yāʾ forms alike.
    .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
    .replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627')
    .replace(/\u0649/g, '\u064A')
    .replace(/\u0629/g, '\u0647')
    .replace(/[^a-z0-9α-ω؀-ۿ ]+/g, ' ')
    .trim();

function buildIndex(): Entry[] {
  const s = services.peek()!;
  const arabicOn = settings.peek().arabic;
  const out: Entry[] = [];
  for (const id of BODY_IDS) {
    // With Arabic on, by the Arabic names their cards give (al-Qamar, الْقَمَر) too.
    const ar = arabicOn ? BODY_ARABIC[id] : undefined;
    out.push({ label: id, sub: id === 'Sun' ? 'Our star' : id === 'Moon' ? 'Earth’s moon' : 'Planet', icon: id === 'Moon' ? 'moon' : id === 'Sun' ? 'sun' : 'planet', sel: { kind: 'body', id }, keys: [norm(id), ...(ar ? [norm(ar.tr), norm(ar.ar)] : [])], rank: 100 });
  }
  const cat = s.engine.stars;
  for (const [i, info] of cat.info) {
    if (!info.name && !info.aka && cat.mag[i] > 4.5) continue;
    const desig = starDesignation(cat, i);
    const ar = arabicOn && info.name ? ARABIC_STAR_NAMES[info.name] : undefined;
    // An older name the IAU never adopted finds the star, but is not shown (A7).
    const keys = [norm(info.name), norm(info.aka), norm(desig), ar ? norm(ar.tr) : '', ar ? norm(ar.ar) : ''].filter(Boolean);
    out.push({ label: info.name || desig, sub: `${info.name ? desig + ' · ' : ''}star`, icon: 'star', sel: { kind: 'star', index: i }, keys, rank: 60 - cat.mag[i] * 5 });
  }
  s.engine.cons.constellations.forEach((c, k) => {
    if (out.some((e) => e.sel.kind === 'constellation' && e.label === c.name)) return;
    const meaning = constellationMeaning(c);
    out.push({ label: c.name, sub: meaning ? `Constellation · ${meaning}` : 'Constellation', icon: 'star', sel: { kind: 'constellation', index: k }, keys: [norm(c.name), norm(c.en), norm(meaning), norm(c.gen), c.id.toLowerCase(), arabicOn && c.ar ? norm(c.ar) : ''], rank: 70 - c.rank * 5 });
  });
  // The catalogue number with and without its space ("ngc2264"), as typed and as voice search hears it (src/live/voice.ts).
  s.engine.dsos.forEach((d, k) => {
    out.push({ label: d.name ? `${d.id} · ${d.name}` : d.id, sub: `${d.t}${d.mag != null ? `, mag ${d.mag}` : ''}`, icon: 'eye', sel: { kind: 'dso', index: k }, keys: [norm(d.id), norm(d.cat), norm(d.cat).replace(/ /g, ''), d.name ? norm(d.name) : '', d.m ? `m${d.m}` : '', d.m ? `messier ${d.m}` : ''].filter(Boolean), rank: d.m ? 45 : 30 });
  });
  for (const m of s.sats.meta.values()) {
    const station = m.kind === 'station';
    out.push({ label: longName(m), sub: `${station ? 'Space station' : m.kind === 'starlink' ? 'Starlink satellite' : 'Satellite'} · NORAD ${m.id}`, icon: 'satellite', sel: { kind: 'satellite', id: m.id }, keys: [norm(m.name), norm(displayName(m)), norm(longName(m)), String(m.id), m.intl.toLowerCase()], rank: station ? 95 : m.stdMag < 3 ? 40 : 20 });
  }
  for (const a of s.small.asteroids) {
    out.push({ label: a.name, sub: `Asteroid${a.diameterKm ? ` · ${Math.round(a.diameterKm)} km across` : ''}`, icon: 'planet', sel: { kind: 'small', name: a.name }, keys: [norm(a.name), a.id ? String(a.id) : ''], rank: 42 });
  }
  for (const c of s.small.comets) {
    out.push({ label: c.name, sub: 'Comet', icon: 'meteor', sel: { kind: 'small', name: c.name }, keys: [norm(c.name)], rank: 25 });
  }
  for (const sh of s.meteors.showers) {
    out.push({ label: sh.name, sub: `Meteor shower · ${sh.code}`, icon: 'meteor', sel: { kind: 'shower', code: sh.code }, keys: [norm(sh.name), sh.code.toLowerCase()], rank: 50 });
  }
  // The Arabian sky's names: with Arabic on, or with the Arabian sky chosen whatever the setting.
  const ar = arabicOn || settings.peek().skyCulture === 'arabic' ? s.engine.arabic : null;
  if (ar) {
    ar.sky.figures.forEach((f, k) => {
      if (f.manzil) return; // found as its station, below
      const en = knownMeaning(f.en) ? [f.en] : [];
      out.push({ label: `${f.tr} · ${f.ar}`, sub: [`Arabian ${f.kind === 'constellation' ? 'star group' : 'asterism'}`, ...en].join(' · '), icon: 'star', sel: { kind: 'arabic', index: k }, keys: [norm(f.tr), norm(f.ar), ...en.map(norm)], rank: 36 });
    });
    ar.sky.lunar.names.forEach((n, k) => {
      const en = knownMeaning(n.en) ? [n.en] : [];
      out.push({ label: `${n.tr} · ${n.ar}`, sub: [`Lunar station ${k + 1} of 28`, ...en].join(' · '), icon: 'moon', sel: { kind: 'manzil', index: k }, keys: [norm(n.tr), norm(n.ar), ...en.map(norm), 'manzil', 'manazil', 'lunar station', 'lunar mansion'], rank: 34 });
    });
    for (const [i, names] of ar.starNames) {
      out.push({ label: `${names[0].tr} · ${names[0].ar}`, sub: `Arabian name of ${starDesignation(cat, i)} · ${names[0].en}`, icon: 'star', sel: { kind: 'star', index: i }, keys: names.flatMap((n) => [norm(n.tr), norm(n.ar), norm(n.en)]), rank: 33 });
    }
  }
  // Figures of the sky culture being shown (Norse, Chinese…).
  const cu = s.engine.culture;
  if (cu && settings.peek().skyCulture === cu.data.id) {
    cu.data.figures.forEach((f, k) => {
      out.push({ label: f.en, sub: `${cu.data.name}${f.native ? ` · ${f.native}` : ''}`, icon: 'star', sel: { kind: 'figure', index: k }, keys: [norm(f.en), norm(f.native), norm(f.pron)].filter(Boolean), rank: 38 });
    });
  }
  // Everyday names people use.
  const ALIASES: [(e: Entry) => boolean, string[]][] = [
    [(e) => e.sel.kind === 'constellation' && e.label === 'Ursa Major', ['plough', 'the plough', 'big dipper', 'saucepan', 'great bear']],
    [(e) => e.sel.kind === 'constellation' && e.label === 'Ursa Minor', ['little dipper', 'little bear']],
    [(e) => e.sel.kind === 'constellation' && e.label === 'Orion', ['orions belt']],
    [(e) => e.sel.kind === 'constellation' && e.label === 'Cassiopeia', ['the w', 'w shape']],
    [(e) => e.label === 'Polaris', ['north star', 'pole star']],
    [(e) => e.label === 'Sirius', ['dog star']],
    [(e) => e.sel.kind === 'body' && e.label === 'Venus', ['evening star', 'morning star']],
    [(e) => e.sel.kind === 'dso' && e.label.startsWith('M45'), ['pleiades', 'thurayya', 'al-thurayya']],
    [(e) => e.sel.kind === 'dso' && e.label.startsWith('M31'), ['andromeda galaxy', 'andromeda']],
    [(e) => e.sel.kind === 'satellite' && e.sel.id === 25544, ['space station', 'international space station', 'iss']],
    [(e) => e.sel.kind === 'body' && e.label === 'Mars', ['red planet']],
  ];
  for (const [match, keys] of ALIASES) {
    const e = out.find(match);
    if (e) e.keys.push(...keys.map(norm));
  }
  return out;
}

/** Best matches for a query (already normalised), with scores. */
function rank(index: Entry[], nq: string): [Entry, number][] {
  const scored: [Entry, number][] = [];
  for (const e of index) {
    let best = 0;
    for (const k of e.keys) {
      if (!k) continue;
      if (k === nq) best = Math.max(best, 100);
      else if (k.startsWith(nq)) best = Math.max(best, 80);
      else if (k.split(' ').some((w) => w.startsWith(nq))) best = Math.max(best, 65);
      else if (nq.length > 2 && k.includes(nq)) best = Math.max(best, 40);
    }
    if (best) scored.push([e, best + e.rank / 10]);
  }
  return scored.sort((a, b) => b[1] - a[1]);
}

export function SearchPanel() {
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const [arabicReady, setArabicReady] = useState(!!services.peek()?.engine.arabic);
  useEffect(() => {
    if (!arabicReady && (settings.peek().arabic || settings.peek().skyCulture === 'arabic')) void services.peek()?.engine.ensureArabic().then(() => setArabicReady(!!services.peek()?.engine.arabic));
  }, []);
  // Satellites, comets and asteroids can arrive (or be replaced) while the panel is open: the index follows them.
  const satRev = satStatus.value.rev, smallRev = smallBodiesRev.value;
  const index = useMemo(buildIndex, [arabicReady, satRev, smallRev]);
  useEffect(() => {
    if (voiceSupported()) void prepareVoice(index.filter((e) => e.rank >= 45).map((e) => e.label.split(' · ').pop()!).slice(0, 300));
  }, [index]);
  useEffect(() => {
    setTimeout(() => input.current?.focus(), 250);
  }, []);
  const nq = norm(q);
  const results = useMemo(() => (nq ? rank(index, nq).slice(0, 40).map((x) => x[0]) : []), [nq, index]);
  // The parts of Ahillah in More, found by name too, so "learn" or "calendar" opens them (DECISIONS 108).
  const pages = findPages(q);
  const s = services.value!;
  const [listening, setListening] = useState(false);
  const [voiceNote, setVoiceNote] = useState('');
  const stopRef = useRef<(() => void) | null>(null);
  useEffect(() => () => stopRef.current?.(), []);
  /** Everything bright that is up now, for "what's that bright star in the west". */
  const brightNow = (): Candidate<Selection>[] => {
    const e = s.engine;
    const out: Candidate<Selection>[] = [];
    const place = (v: Vec3) => enuToAltAz(refractEnu(v));
    for (const b of e.bodies) {
      if (b.id === 'Sun') continue;
      const h = place(e.frame.toEnu(b.dir));
      out.push({ item: { kind: 'body', id: b.id }, alt: h.alt, az: h.az, mag: b.mag, type: b.id === 'Moon' ? 'moon' : 'planet', bv: b.id === 'Mars' ? 1.4 : undefined });
    }
    const cat = e.stars;
    for (let i = 0; i < cat.mag.length && cat.mag[i] < 2.6; i++) {
      const h = place(e.frame.toEnu(e.starDir(i)));
      out.push({ item: { kind: 'star', index: i }, alt: h.alt, az: h.az, mag: cat.mag[i], type: 'star', bv: cat.bv[i] });
    }
    return out;
  };
  const act = (said: string, alternatives: string[] = []) => {
    // Of the recogniser's guesses, prefer one that names something in the sky.
    let it = parseVoice(said);
    if (it.kind === 'search') {
      const score = (x: VoiceIntent) => (x.kind === 'search' ? rank(index, norm(x.query))[0]?.[1] ?? 0 : 0);
      for (const alt of alternatives.slice(1)) {
        const other = parseVoice(alt);
        if (other.kind !== 'search' || score(other) > score(it) + 10) {
          it = other;
          break;
        }
      }
    }
    if (it.kind === 'panel') return void (panel.value = it.panel);
    if (it.kind === 'now') {
      goLive();
      panel.value = null;
      return;
    }
    if (it.kind === 'point') {
      const hit = pointingAt.peek()?.sel ?? s.engine.pick(s.engine.camera.width / 2, s.engine.camera.height / 2, 60)?.sel;
      panel.value = null;
      if (hit) selection.value = hit;
      return;
    }
    if (it.kind === 'face') {
      const name = directionWords(it.az);
      if (viewMode.peek() === 'ar') return void setVoiceNote(`Turn until the ${name} marker on the horizon is in front of you.`);
      panel.value = null;
      const az = (it.az * Math.PI) / 180, el = (20 * Math.PI) / 180;
      s.engine.flyTo([Math.cos(el) * Math.sin(az), Math.cos(el) * Math.cos(az), Math.sin(el)], 90, 1.2);
      return;
    }
    if (it.kind === 'bright') {
      const hit = brightestMatch(brightNow(), it);
      if (!hit) {
        const where = it.az != null ? ` in the ${directionWords(it.az)}` : '';
        return void setVoiceNote(`Nothing that bright is up${where} right now.`);
      }
      panel.value = null;
      selection.value = hit.item;
      return;
    }
    setQ(it.query);
    // A clear match opens straight away; otherwise the list shows what might have been meant.
    const top = rank(index, norm(it.query))[0];
    if (top && top[1] >= 80) choose(top[0]);
  };
  const toggleVoice = () => {
    if (listening) {
      stopRef.current?.();
      return;
    }
    // Stop any narration first: on iPhones, recognition can hang while audio is playing.
    stopNarration();
    setVoiceNote('');
    setListening(true);
    let finalText = '', alts: string[] = [];
    stopRef.current = listen({
      onText: (t, final, alternatives) => {
        setQ(t);
        if (final) {
          finalText = t;
          alts = alternatives;
        }
      },
      onError: (m) => setVoiceNote(m),
      onEnd: () => {
        setListening(false);
        stopRef.current = null;
        if (finalText) act(finalText, alts);
      },
    });
  };
  const choose = (e: Entry) => {
    panel.value = null;
    // Pointing at the sky, the card starts small so the sky and the guide to the object stay clear; tapping it
    // opens the whole card. Otherwise the card frames the object above itself once it opens.
    infoMinimised.value = viewMode.peek() === 'ar';
    selection.value = e.sel;
  };
  const suggestions: Entry[] = useMemo(() => {
    const picks: Entry[] = [];
    for (const id of ['Moon', 'Jupiter', 'Saturn', 'Venus', 'Mars']) {
      const e = index.find((x) => x.sel.kind === 'body' && x.label === id);
      if (e) picks.push(e);
    }
    const iss = index.find((x) => x.sel.kind === 'satellite' && x.sel.id === 25544);
    if (iss) picks.unshift(iss);
    for (const n of ['Polaris', 'Vega', 'Sirius', 'Orion', 'Ursa Major', 'M31 · Andromeda Galaxy', 'M45 · Pleiades']) {
      const e = index.find((x) => x.label === n);
      if (e) picks.push(e);
    }
    return picks;
  }, [index]);
  const list = nq ? results : suggestions;
  // Before typing: suggestions split into what is up now and what is not.
  // On the day Saudi Arabia looks for the crescent, nothing is said of where the Moon is (src/live/saudi.ts): it is left
  // out of the suggestions, which sort by up or down, and found without its place.
  const looking = !!lookingFor(simNow().getTime(), place.value);
  const isMoon = (sel: Selection) => sel.kind === 'body' && sel.id === 'Moon';
  const rows = list.filter((e) => !(looking && !nq && isMoon(e.sel))).map((e) => {
    const loc = looking && isMoon(e.sel) ? null : s.engine.locate(e.sel);
    return { e, w: loc ? enuToAltAz(refractEnu(loc.enu)) : null };
  });
  const groups = nq
    ? [{ title: pages.length ? 'In the sky' : null, rows }]
    : [{ title: 'Up now', rows: rows.filter((r) => r.w && r.w.alt >= -0.5) }, { title: 'Below the horizon', rows: rows.filter((r) => !r.w || r.w.alt < -0.5) }];
  const first = groups.flatMap((g) => g.rows)[0]?.e;
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Search">
      <h2 class="sr-only">Search</h2>
      <label class={`search-box ${listening ? 'listening' : ''}`}>
        <Icon name="search" size={18} />
        <input ref={input} value={q} aria-label="Search the sky" onInput={(e) => setQ((e.target as HTMLInputElement).value)} placeholder={listening ? 'Listening…' : 'Planets, stars…'} enterKeyHint="search" autocomplete="off" autocapitalize="off" spellcheck={false}
          onKeyDown={(e) => { if (e.key !== 'Enter') return; if (pages[0]) pages[0].open(); else if (first) choose(first); }} />
        {voiceSupported() && (
          <button class={`mic-btn ${listening ? 'on' : ''}`} aria-label={listening ? 'Stop listening' : 'Search by voice'} onClick={toggleVoice}>
            <Icon name="mic" size={18} />
          </button>
        )}
      </label>
      {voiceNote && <div class="muted small pad">{voiceNote}</div>}
      {!q && voiceSupported() && !listening && (
        <div class="search-hint">
          <p>Or tap the microphone and ask: “show me Jupiter”, “what’s that bright star in the west?” or “when does the space station pass?”</p>
          <p class="fine">{voiceOnDevice() ? 'Your speech is recognised on this device.' : 'Your browser recognises the speech; in Chrome that is done by Google.'}</p>
        </div>
      )}
      {pages.length > 0 && (
        <>
          <h3 class="row-label">In Ahillah</h3>
          <div class="results">
            {pages.map((p) => (
              <button class="result" key={p.label} onClick={p.open}>
                <span class="result-icon"><Icon name={p.icon} size={20} /></span>
                <span class="result-text">
                  <span class="result-label">{p.label}</span>
                  <span class="result-sub">{p.sub}</span>
                </span>
                <Icon name="chevron" size={16} />
              </button>
            ))}
          </div>
        </>
      )}
      {groups.map((g) => (
        <Fragment key={g.title ?? 'all'}>
          {g.title && g.rows.length > 0 && <h3 class="row-label">{g.title}</h3>}
          <div class="results">
            {g.rows.map(({ e, w }) => (
              <button class="result" key={`${e.sel.kind}-${e.label}-${e.sub}`} onClick={() => choose(e)}>
                <span class="result-icon"><Icon name={e.icon} size={20} /></span>
                <span class="result-text">
                  <span class="result-label">{e.label}</span>
                  <span class="result-sub">{e.sub}{w ? ` · ${whereInSky(w.alt, w.az).toLowerCase()}` : ''}</span>
                </span>
                <Icon name="chevron" size={16} />
              </button>
            ))}
          </div>
        </Fragment>
      ))}
      {nq && !results.length && !pages.length && <div class="empty">Nothing found for “{q}”. Try a planet, a star or a constellation.</div>}
    </Sheet>
  );
}

/** Constellation for a J2000 direction (used by other panels). */
export function constellationOf(dir: [number, number, number]) {
  const { ra, dec } = vecToRaDec(dir);
  return constellationName(ra, dec);
}
